import type { ICartGroup } from '@/api/types/cart';

type ApplyCartSelectionToGroupsInput = {
  groups: readonly ICartGroup[];
  eligibleCartIds: readonly number[];
  selectedCartIds: readonly number[];
};

const isValidCartId = (cartId: unknown): cartId is number =>
  Number.isSafeInteger(cartId) && Number(cartId) > 0;

const isInactiveGiftGroup = (group: ICartGroup) =>
  group.type === 4 && group.is_allow_settlement !== 1;

const getGroupCartId = (group: ICartGroup): number | null =>
  group.carts?.find((cart) => isValidCartId(cart.cart_id))?.cart_id ?? null;

export const collectEligibleCartIds = (groups: readonly ICartGroup[]): number[] => {
  const eligibleCartIds = new Set<number>();

  for (const group of groups) {
    if (isInactiveGiftGroup(group)) {
      continue;
    }

    const cartId = getGroupCartId(group);
    if (cartId !== null) {
      eligibleCartIds.add(cartId);
    }
  }

  return Array.from(eligibleCartIds);
};

export const applyCartSelectionToGroups = ({
  groups,
  eligibleCartIds,
  selectedCartIds,
}: ApplyCartSelectionToGroupsInput): ICartGroup[] => {
  const eligibleIdSet = new Set(eligibleCartIds);
  const selectedIdSet = new Set(selectedCartIds);

  return groups.map((group) => {
    const cartId = getGroupCartId(group);
    const disabled = cartId === null || !eligibleIdSet.has(cartId) || isInactiveGiftGroup(group);

    return {
      ...group,
      disabled,
      selected: !disabled && selectedIdSet.has(cartId),
    };
  });
};

export const collectSelectedCartIds = (groups: readonly ICartGroup[]): number[] => {
  const selectedCartIds = new Set<number>();

  for (const group of groups) {
    if (!group.selected || group.disabled) {
      continue;
    }

    const cartId = getGroupCartId(group);
    if (cartId !== null) {
      selectedCartIds.add(cartId);
    }
  }

  return Array.from(selectedCartIds);
};
